import React, { useState, useEffect } from "react";
import * as Form from '@radix-ui/react-form';
import { Button } from '@radix-ui/themes';
import { TextInput } from '@tremor/react';
import { Card, Title, Text } from '@tremor/react';
import { KeyRound, AlertCircle, CheckCircle, Eye, EyeOff, Mail } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from './api.js';
import { BackIcon, ICON_SIZE, SendIcon } from "./actionIcons";

export default function ResetPassword() {
  const [newPassword, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  //Invitation rather than a reset. Same exchange and the same endpoint — only
  //the wording differs, because "Reset Password" is a confusing thing to read
  //when you have never had one. Set by the link in the invite email.
  const [isWelcome, setIsWelcome] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token and email from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get('token');
    const userEmail = queryParams.get('email');
    setIsWelcome(queryParams.get('welcome') === '1');

    if (resetToken) {
      setToken(resetToken);
    } else {
      setError("Invalid or missing link. Please request a new one.");
    }
    
    if (userEmail) {
      setEmail(userEmail);
    }
  }, [location]);

  const validatePassword = () => {
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!validatePassword()) {
      return;
    }

    setIsLoading(true);

    try {
      // Replace with your actual API endpoint
      const res = await apiFetch("/reset/reset-password", { body: { 
          token,
          email,
          newPassword 
        } });

      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.message || "Password reset failed. Please try again or request a new reset link.");
      }
    } catch (err) {
      setError("Failed to connect to the server. Please check your internet connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="max-w-md mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div></div>
          <Title className="text-center">{isWelcome ? "Set Your Password" : "Reset Password"}</Title>
          <Button variant="ghost" onClick={() => navigate("/login")}><BackIcon size={ICON_SIZE} />
            Back to Login
          </Button>
        </div>

        {!token && (
          <div className="text-center space-y-4 py-4">
            <div className="flex justify-center">
              <AlertCircle size={48} color="red" />
            </div>
            <Text className="text-red-600">
              {error}
            </Text>
            <Button 
              className="mt-4" 
              onClick={() => navigate("/forgot-password")}
            ><SendIcon size={ICON_SIZE} />
              Request New Reset Link
            </Button>
          </div>
        )}

        {token && success ? (
          <div className="text-center space-y-4 py-4">
            <div className="flex justify-center">
              <CheckCircle size={48} color="green" />
            </div>
            <Text className="text-green-600">
              {isWelcome ? "Your account is ready!" : "Your password has been successfully reset!"}
            </Text>
            <Text>
              You can now log in with your new password.
            </Text>
            <Button 
              className="mt-4" 
              onClick={() => navigate("/login")}
            ><BackIcon size={ICON_SIZE} />
              Go to Login
            </Button>
          </div>
        ) : token && (
          <Form.Root className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Text>
                {isWelcome
                  ? "Welcome! Choose a password to finish setting up your account. It should be at least 8 characters long."
                  : "Enter your new password below. Your password should be at least 8 characters long."}
              </Text>

              <Form.Field name="email" className="mt-4">
                <div className="flex justify-between">
                  <Form.Label className="text-sm font-medium">Email Address</Form.Label>
                </div>
                <Form.Control asChild>
                  <TextInput
                    type="email"
                    name="email"
                    value={email}
                    disabled={true}
                    placeholder="Your email address"
                    icon={Mail}
                  />
                </Form.Control>
              </Form.Field>
              
              <Form.Field name="password" className="mt-4">
                <div className="flex justify-between">
                  <Form.Label className="text-sm font-medium">New Password</Form.Label>
                </div>
                <div className="relative">
                  <Form.Control asChild>
                    <TextInput
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={newPassword}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      error={!!error}
                      icon={KeyRound}
                    />
                  </Form.Control>
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </Form.Field>

              <Form.Field name="confirmPassword" className="mt-4">
                <div className="flex justify-between">
                  <Form.Label className="text-sm font-medium">Confirm Password</Form.Label>
                </div>
                <div className="relative">
                  <Form.Control asChild>
                    <TextInput
                      type={showPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      error={!!error}
                      icon={KeyRound}
                    />
                  </Form.Control>
                </div>
                {error && (
                  <div className="flex items-center mt-2 text-sm text-red-500">
                    <AlertCircle size={14} className="mr-1" />
                    <Text color="red">{error}</Text>
                  </div>
                )}
              </Form.Field>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : (isWelcome ? "Set Password" : "Reset Password")}
              </Button>
            </div>
          </Form.Root>
        )}
      </Card>
    </div>
  );
}